import { startOfDayInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { downloadExportFile } from '@/helpers/betExport';
import { createOptionalSportFilter, resetOptionalSportFilter } from '@/helpers/sportFilter';
import { LeagueDailyCoverageItem } from '@/models/leagueDailyCoverage';
import { createLeagueDailyCoveragePageHeaders } from '@/pages/admin/leagueDailyCoverage/LeagueDailyCoveragePageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { leagueDailyCoverageService } from '@/services/leagueDailyCoverageService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import { ILeagueDailyCoverageFilters } from '@/types/leagueDailyCoverage';
import type { DataTableHeader } from '@/types/vuetify';

function createLeagueDailyCoverageStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<LeagueDailyCoverageItem[]>([]);
    const total = ref(0);
    const loading = ref(false);
    const isExporting = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<ILeagueDailyCoverageFilters>({
        date: {
            value: startOfDayInUserTimeZone(),
            title: 'leagueDailyCoverage.filters.date',
            allowSaveState: true,
            disableClear: true,
        },
        customLeagueSportId: createOptionalSportFilter(),
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 500,
        rowsPerPageItems: [500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        resetOptionalSportFilter(filters.customLeagueSportId);
        filters.date.value = startOfDayInUserTimeZone();
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createLeagueDailyCoveragePageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'leagueDailyCoverage',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<ILeagueDailyCoverageFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: [],
                    page: 1,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
            };

            const response = await leagueDailyCoverageService.list(params);
            items.value = response.data.rows.map((row) => new LeagueDailyCoverageItem(row));
            total.value = response.data.total;
            options.value.sortBy = [];
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    async function exportData() {
        isExporting.value = true;
        try {
            const exportColumns = tableHeaders.value
                .filter((header) => header.key && header.title)
                .map((header) => ({
                    key: header.key,
                    title: header.title,
                }));

            const params: IListParams<ILeagueDailyCoverageFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: [],
                    page: 1,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
                columns: JSON.stringify(exportColumns),
            };

            const response = await leagueDailyCoverageService.export(params);
            downloadExportFile(response, 'league-daily-coverage.xlsx');
        } finally {
            isExporting.value = false;
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        isExporting,
        options,
        loadData,
        exportData,
        clearData,
        stats: undefined,
        hasRefresh: true,
        hasExport: true,
    };
}

export function useLeagueDailyCoverageStore(storeId: string = 'leagueDailyCoverage') {
    const useStore = defineStore(storeId, () => createLeagueDailyCoverageStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'leagueDailyCoverage', []),
    });
    return useStore();
}

export type LeagueDailyCoverageStoreSetup = ReturnType<typeof useLeagueDailyCoverageStore>;
